import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import { MenuErrorSeverity, type MenuErrorOccurrence } from 'api/menuErrors'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'

const t = buildTranslate('pos_configurations.menu_errors_list')

// Severity icon mapping mirrors the badge palette used in MenuCard so
// the colour cue is consistent between the card header (badge counts)
// and the individual row icons.
const severityIcon = (severity: MenuErrorSeverity) => {
  if (severity === MenuErrorSeverity.Error) {
    return { className: 'fa fa-times-circle', color: palette.cayenne50 }
  }
  return { className: 'fa fa-exclamation-circle', color: palette.eggYolk50 }
}

type Props = {
  occurrence: MenuErrorOccurrence
}

const ErrorRow = ({ occurrence }: Props) => {
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const [expanded, setExpanded] = useState(false)
  const icon = severityIcon(occurrence.severity)

  // Copies the raw POS item UUID so an operator (or whoever runs their
  // Toast dashboard) can paste it into Toast's item search. Diana's
  // 2026-05-29 call: the UUID itself isn't actionable inline, but a
  // one-click copy turns it into a usable escalation hand-off. A direct
  // Toast deep-link would be better still, but that needs a Toast
  // item-URL pattern + a per-merchant restaurant GUID the menu_errors
  // payload doesn't carry yet — deferred.
  const severityLabel = t(`severity_label.${occurrence.severity}`) as string

  // `navigator.clipboard.writeText` rejects on insecure origins, in some
  // sandboxes, and when the OS denies permission. Catch it so a failed
  // copy shows an error flash instead of an unhandled promise rejection
  // and a silent no-op for the operator.
  const copyItemId = async (uuid: string, name: string) => {
    try {
      await navigator.clipboard.writeText(uuid)
      dispatch(
        alert({
          key: 'success',
          message: t('item_id_copied', { name }) as string,
          timeout: 5,
        })
      )
    } catch {
      dispatch(
        alert({
          key: 'danger',
          message: t('item_id_copy_failed') as string,
          timeout: 5,
        })
      )
    }
  }
  // The wire `message` is the full raw validator string ("Item '<name>'
  // (<uuid>): <reason>"); `description` is the BE's prefix-stripped reason
  // (DATA-5330). Prefer the reason as the row title — the name/uuid live
  // in the expanded items list — and fall back to the raw string for
  // occurrences the BE couldn't parse.
  const title = occurrence.description ?? occurrence.message
  const expandLabel = (
    expanded
      ? t('collapse_row_aria', {
          severity: severityLabel,
          message: title,
        })
      : t('expand_row_aria', {
          severity: severityLabel,
          message: title,
        })
  ) as string

  return (
    <div className={css({ borderTop: `1px solid ${palette.grey20}` })}>
      <button
        type="button"
        onClick={() => setExpanded(prev => !prev)}
        aria-expanded={expanded}
        aria-label={expandLabel}
        // No project-wide `unstyled-button` class exists — explicitly
        // strip the default browser chrome (border, background) so the
        // row reads as part of the parent card, not as a nested card.
        className={`d-flex align-items-start w-100 p-m ${css({
          appearance: 'none',
          background: 'transparent',
          border: 'none',
          padding: '16px',
          margin: 0,
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          cursor: 'pointer',
          ':hover': { backgroundColor: palette.grey05 },
          ':focus-visible': {
            outline: `2px solid ${palette.belizeHole}`,
            outlineOffset: '-2px',
          },
        })}`}
      >
        <i
          // Decorative: the severity is already announced via the toggle
          // button's aria-label ("Show {severity} details for …"), which
          // overrides descendant content for AT — so a role="img"+aria-label
          // here would be dead weight, not a second announcement.
          className={`${icon.className} mr-m ${css({
            color: icon.color,
            fontSize: '20px',
            marginTop: '2px',
          })}`}
          aria-hidden="true"
        />
        <div className="flex-grow-1">
          <p
            className={`body-text-3 mb-0 ${css({
              color: palette.grey90,
              wordBreak: 'break-word',
              fontWeight: 600,
              // The BE emits reasons verbatim from the validator strings,
              // which are lowercase ("missing image"). Sentence-case the
              // displayed title without mutating the data (grouping keys
              // and aria text stay byte-identical to the wire value).
              '::first-letter': { textTransform: 'uppercase' },
            })}`}
          >
            {title}
          </p>
        </div>
        <i
          className={`fa fa-chevron-${expanded ? 'up' : 'down'} ml-m ${css({
            color: palette.grey50,
            fontSize: '14px',
            marginTop: '6px',
          })}`}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <div
          className={`px-m pb-m ${css({
            paddingLeft: '56px',
          })}`}
        >
          {occurrence.items && occurrence.items.length > 0 ? (
            <ul
              className={`list-unstyled mb-0 p-m ${css({
                backgroundColor: palette.grey05,
                borderRadius: '4px',
              })}`}
            >
              {occurrence.items.map(item => (
                <li
                  key={item.uuid}
                  className={`d-flex align-items-center mb-xxs ${css({
                    gap: '8px',
                  })}`}
                >
                  {/* Item name leads (bold, grey90); the UUID is
                      de-emphasized — monospace + smaller + lighter — so
                      the eye lands on the name first (Diana, option B). */}
                  <span
                    className={`body-text-3 ${css({
                      color: palette.grey90,
                      fontWeight: 600,
                    })}`}
                  >
                    {item.name}
                  </span>
                  <span
                    className={css({
                      color: palette.grey50,
                      fontFamily: 'monospace',
                      fontSize: '12px',
                    })}
                  >
                    {item.uuid}
                  </span>
                  {/* Copy-ID button (Diana, option A) — turns the
                      otherwise-inert UUID into a one-click escalation
                      hand-off. Reset button chrome inline; no
                      project-wide unstyled-button utility exists. */}
                  <button
                    type="button"
                    onClick={() => copyItemId(item.uuid, item.name)}
                    aria-label={
                      t('copy_item_id_aria', { name: item.name }) as string
                    }
                    className={css({
                      appearance: 'none',
                      background: 'transparent',
                      border: 'none',
                      padding: '2px',
                      cursor: 'pointer',
                      // Standard text colour (grey90), not the faded grey50 —
                      // the copy affordance should read as actionable, not
                      // muted like the UUID it sits beside (Diana, 2026-05-29).
                      color: palette.grey90,
                      lineHeight: 1,
                      ':hover': { color: palette.belizeHole },
                      ':focus-visible': {
                        outline: `2px solid ${palette.belizeHole}`,
                        outlineOffset: '2px',
                      },
                    })}
                  >
                    <i className="fa fa-copy" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className={`body-text-4 mb-0 ${css({ color: palette.grey70 })}`}>
              {t('no_items') as string}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default ErrorRow
